import React, { createContext, Dispatch, ReactNode, useContext, useReducer } from 'react';

const ReviewStateContext = createContext<ReviewContainer | undefined>(undefined);
const ReviewDispatchContext = createContext<Dispatch<ReviewAction> | undefined>(undefined);

interface ReviewContainer {
    review?: any;
    isNorthStarEnabled?: boolean;
}

export interface ReviewAction {
    type: ReviewActionType;
    review?: any;
}

export enum ReviewActionType {
    SetReview,
    UpdateReview,
}

interface ReviewProviderProps {
    children: ReactNode;
    review?: any;
    isNorthStarEnabled?: boolean;
}

const reviewReducer = (reviewContainer: ReviewContainer, action: ReviewAction): ReviewContainer => {
    switch (action.type) {
        case ReviewActionType.SetReview: {
            if (!action.review) {
                throw new Error('SetReview: required action arg missing: review');
            }

            return { ...reviewContainer, review: action.review };
        }
        case ReviewActionType.UpdateReview: {
            if (!action.review) {
                throw new Error('UpdateReview: required action arg missing: review');
            }

            return { ...reviewContainer, review: { ...reviewContainer.review, ...action.review } };
        }
        default:
            throw new Error(`Review: unhandled action: ${action.type}`);
    }
};

export const ReviewProvider = ({ children, review, isNorthStarEnabled }: ReviewProviderProps): JSX.Element => {
    const [state, dispatch] = useReducer(reviewReducer, {
        review,
        isNorthStarEnabled,
    });

    return (
        <ReviewStateContext.Provider value={state}>
            <ReviewDispatchContext.Provider value={dispatch}>{children}</ReviewDispatchContext.Provider>
        </ReviewStateContext.Provider>
    );
};

export const useReviewState = (): ReviewContainer => {
    const context = useContext(ReviewStateContext);

    if (context === undefined) {
        throw new Error('useReviewState must be used within a ReviewProvider');
    }

    return context;
};

export const useReviewDispatch = (): Dispatch<ReviewAction> => {
    const context = useContext(ReviewDispatchContext);

    if (context === undefined) {
        throw new Error('useReviewDispatch must be used within a ReviewProvider');
    }

    return context;
};

export const useReview = (): [ReviewContainer, Dispatch<ReviewAction>] => {
    return [useReviewState(), useReviewDispatch()];
};
